import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FOUNDERS, TEAM_LEADS, type FounderEntry, type TeamLead } from "@/lib/brand";
import { useLocalized } from "@/hooks/useLocalized";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHorizontalRail } from "./rail/useHorizontalRail";
import {
    RailArrows,
    RailEdgeFades,
    RailEyebrow,
    RailPagination,
    RailProgressBar,
    RailScrollbarHideStyle,
    railContainerClass,
} from "./rail/RailControls";

const FOUNDER_ACCENT_HUES = [260, 190, 320];
const TEAM_LEAD_ACCENT_HUES = [160, 30];

const FounderCard = ({
    founder,
    index,
    total,
    accentHue,
    staggerInView,
}: {
    founder: FounderEntry;
    index: number;
    total: number;
    accentHue: number;
    staggerInView: boolean;
}) => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const background = useLocalized({ ar: founder.backgroundAr, en: founder.backgroundEn });
    const quote = useLocalized({ ar: founder.quoteAr, en: founder.quoteEn });
    const name = lang === "en" ? founder.nameEn : founder.nameAr;
    const title = lang === "en" ? founder.titleEn : founder.titleAr;
    const secondaryName = lang === "en" ? founder.nameAr : founder.nameEn;
    const secondaryTitle = lang === "en" ? founder.titleAr : founder.titleEn;
    const accent = `hsl(${accentHue} 90% 60%)`;
    const accentDim = `hsl(${accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${accentHue} 90% 60% / 0.18)`;
    const monogramIndex = String(index + 1).padStart(2, "0");

    return (
        <motion.article
            className="group relative h-full"
            initial={{ opacity: 0, y: 30 }}
            animate={staggerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
                duration: 0.55,
                delay: 0.1 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -4 }}
        >
            <div
                aria-hidden
                className="absolute -inset-px rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `conic-gradient(from 140deg at 50% 50%, ${accent}, transparent 35%, transparent 65%, ${accent})`,
                    padding: "1px",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                }}
            />

            <div
                className="glass-v2 rounded-[32px] p-7 md:p-8 relative overflow-hidden h-full flex flex-col"
                data-cursor="hover"
            >
                <span
                    aria-hidden
                    className="absolute -top-4 end-4 font-v2-display leading-none select-none pointer-events-none text-[120px] md:text-[160px] text-white/[0.04] tracking-tighter"
                >
                    {monogramIndex}
                </span>

                <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(600px circle at 50% 0%, ${accentSoft}, transparent 60%)`,
                    }}
                />

                <div
                    aria-hidden
                    className="absolute top-0 inset-x-8 h-px opacity-60 pointer-events-none"
                    style={{
                        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                    }}
                />

                <div className="relative flex-1 flex flex-col">
                    <header className="flex items-center gap-4 mb-6">
                        <div className="relative shrink-0">
                            <div
                                aria-hidden
                                className="absolute -inset-1.5 rounded-[24px] opacity-0 group-hover:opacity-60 transition-opacity duration-500 blur-md"
                                style={{ background: accent }}
                            />
                            <div
                                className="relative w-[84px] h-[84px] md:w-[96px] md:h-[96px] rounded-[20px] overflow-hidden"
                                style={{
                                    border: `1.5px solid ${accentDim}`,
                                    boxShadow: `0 20px 50px -15px rgba(0,0,0,0.6), 0 0 40px ${accentSoft}`,
                                }}
                            >
                                <img
                                    src={founder.photo}
                                    alt={name}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                                <div
                                    aria-hidden
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.25))",
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                                <span
                                    className="font-v2-label text-[9px] px-2 py-0.5 rounded-full border"
                                    style={{
                                        background: accentSoft,
                                        borderColor: accentDim,
                                        color: `hsl(${accentHue} 90% 85%)`,
                                    }}
                                >
                                    {monogramIndex}
                                </span>
                                <span className="font-v2-label text-[10px] text-primary/80">
                                    {t("founders_home.role_label")} · {background}
                                </span>
                            </div>
                            <h3 className="font-v2-display text-lg md:text-xl text-white leading-tight mb-1">
                                {name}
                            </h3>
                            <p className="font-v2-display text-[11px] md:text-xs text-white/60">
                                {secondaryName} · {secondaryTitle}
                            </p>
                        </div>
                    </header>

                    <div
                        aria-hidden
                        className="h-px w-full opacity-30 mb-6"
                        style={{
                            background:
                                "linear-gradient(90deg, transparent, hsl(260 90% 65% / 0.8), hsl(190 90% 50% / 0.8), transparent)",
                        }}
                    />

                    <blockquote className="relative flex-1">
                        <p className="text-sm md:text-base text-white/85 leading-[1.85] font-medium">
                            {quote}
                        </p>
                    </blockquote>

                    <div className="mt-auto pt-6 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-v2-label text-[10px] text-white/50 tabular-nums">
                                {String(index + 1).padStart(2, "0")}
                                <span className="text-white/20 mx-1">/</span>
                                {String(total).padStart(2, "0")}
                            </span>
                            <span className="font-v2-label text-[10px] text-white/40 truncate">
                                — {title}
                            </span>
                        </div>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-45 shrink-0"
                            style={{
                                background: accentSoft,
                                border: `1px solid ${accentDim}`,
                            }}
                        >
                            <ArrowUpRight size={14} />
                        </div>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

const TeamLeadCard = ({ lead, accentHue }: { lead: TeamLead; accentHue: number }) => {
    const { lang } = useLanguage();
    const background = useLocalized({ ar: lead.backgroundAr, en: lead.backgroundEn });
    const name = lang === "en" ? lead.nameEn : lead.nameAr;
    const title = lang === "en" ? lead.titleEn : lead.titleAr;
    const accent = `hsl(${accentHue} 90% 60%)`;
    const accentDim = `hsl(${accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${accentHue} 90% 60% / 0.18)`;

    return (
        <div
            className="glass-v2 rounded-[24px] p-5 md:p-6 relative overflow-hidden group flex items-center gap-4 md:gap-5"
            data-cursor="hover"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `radial-gradient(400px circle at 50% 50%, ${accentSoft}, transparent 60%)`,
                }}
            />
            <div
                aria-hidden
                className="absolute top-0 inset-x-6 h-px opacity-60 pointer-events-none"
                style={{
                    background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                }}
            />

            <div className="relative shrink-0">
                <div
                    className="relative w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[16px] overflow-hidden"
                    style={{
                        border: `1.5px solid ${accentDim}`,
                        boxShadow: `0 10px 30px -10px rgba(0,0,0,0.5), 0 0 24px ${accentSoft}`,
                    }}
                >
                    <img
                        src={lead.photo}
                        alt={name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>

            <div className="flex-1 min-w-0 relative">
                <div className="flex items-center gap-2 mb-1">
                    <span
                        className="font-v2-label text-[9px] px-2 py-0.5 rounded-full border"
                        style={{
                            background: accentSoft,
                            borderColor: accentDim,
                            color: `hsl(${accentHue} 90% 85%)`,
                        }}
                    >
                        {background}
                    </span>
                    <span className="font-v2-label text-[10px] text-white/40 truncate">
                        {title}
                    </span>
                </div>
                <h4 className="font-v2-display text-base md:text-lg text-white leading-tight truncate">
                    {name}
                </h4>
            </div>
        </div>
    );
};

const FounderSection = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const isRtl = dir === "rtl";
    const sectionRef = useRef<HTMLElement>(null);
    const staggerInView = useInView(sectionRef, { amount: 0.15, margin: "-40px" });

    const {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    } = useHorizontalRail({
        itemCount: FOUNDERS.length,
        cardSelector: "[data-founder-card]",
        isRtl,
    });

    return (
        <section ref={sectionRef} className="py-20 md:py-32 relative" id="founder">
            <div className="container mx-auto px-6 relative">
                <motion.div
                    className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <RailEyebrow>{t("founders_home.eyebrow")}</RailEyebrow>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("founders_home.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("founders_home.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("founders_home.subtitle")}
                    </p>
                </motion.div>

                <RailPagination
                    itemCount={FOUNDERS.length}
                    activeIndex={activeIndex}
                    onSelect={scrollToIndex}
                    accentHues={FOUNDER_ACCENT_HUES}
                    ariaLabelPrefix="founder"
                />
            </div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.15, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
                {needsScroll && (
                    <>
                        <RailEdgeFades isRtl={isRtl} />
                        <RailArrows
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                            canPrev={canPrev}
                            canNext={canNext}
                            isRtl={isRtl}
                        />
                    </>
                )}

                <div
                    ref={railRef}
                    tabIndex={needsScroll ? 0 : -1}
                    aria-label="Founders carousel"
                    className={railContainerClass(needsScroll)}
                >
                    {FOUNDERS.map((founder, i) => (
                        <div
                            key={founder.key}
                            data-founder-card
                            className="snap-center shrink-0 w-[85vw] sm:w-[55vw] md:w-[320px] lg:w-[340px] xl:w-[380px]"
                        >
                            <FounderCard
                                founder={founder}
                                index={i}
                                total={FOUNDERS.length}
                                accentHue={FOUNDER_ACCENT_HUES[i % FOUNDER_ACCENT_HUES.length]}
                                staggerInView={staggerInView}
                            />
                        </div>
                    ))}
                </div>

                {needsScroll && <RailProgressBar progress={scrollProgress} />}
            </motion.div>

            {TEAM_LEADS.length > 0 && (
                <motion.div
                    className="container mx-auto px-6 mt-12 md:mt-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="max-w-3xl mx-auto">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="font-v2-label text-[10px] text-white/40 tracking-[0.25em] whitespace-nowrap">
                                {t("founders_home.leads_label", { defaultValue: "فريق القيادة" })}
                            </span>
                            <div
                                aria-hidden
                                className="flex-1 h-px"
                                style={{
                                    background:
                                        "linear-gradient(90deg, hsl(0 0% 100% / 0.1), transparent)",
                                }}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {TEAM_LEADS.map((lead, i) => (
                                <TeamLeadCard
                                    key={lead.key}
                                    lead={lead}
                                    accentHue={TEAM_LEAD_ACCENT_HUES[i % TEAM_LEAD_ACCENT_HUES.length]}
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}

            <RailScrollbarHideStyle />
        </section>
    );
};

export default FounderSection;
